import { useState } from 'react';
import { Download, Trash2, Copy, Move, Share2, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useItemListContext } from './ItemListProvider';
import { useItemActions } from '@/hooks/useItemActions';
import { useNotification } from '@/hooks/useNotification';
import { useAppDispatch } from '@/store/hooks';
import { updateResourceProjectPreviewStatus } from '../../apiServices/resourceApi';
import { updateItemPreview } from '../../app/features/project/projectSlice';
import type { NormalizedItem } from './types';

export function SelectionBar() {
  const {
    selectedItemsArray,
    permissions,
    deselectAll,
    selectAll,
    openModal,
    updateIsQuerying,
    onRefresh,
    useCase,
    updateItem,
  } = useItemListContext();
  const { downloadItems } = useItemActions({ updateIsQuerying, onRefresh });
  const { notify } = useNotification();
  const reduxDispatch = useAppDispatch();
  const [previewBusy, setPreviewBusy] = useState(false);

  const count = selectedItemsArray.length;
  const hasSelection = count > 0;
  const allResources = selectedItemsArray.every((i) => i.itemtype === 'resource');
  // Bulk preview/unpreview — moves selected files in/out of the project's work view
  const showPreviewActions = useCase === 'project' && permissions.canEdit && allResources;

  const setPreviewForSelected = async (target: boolean) => {
    if (previewBusy) return;
    // Only renderable files can be previewed; skip ones already in the target state
    const toChange = selectedItemsArray.filter(
      (i) => i.isRendable && !!i.preview !== target,
    );
    if (!toChange.length) {
      notify({ type: 'info', message: target ? 'Selected files are already previewed' : 'Selected files are already hidden' });
      return;
    }
    setPreviewBusy(true);
    // Optimistic — mirror FileCard's per-file toggle
    toChange.forEach((item) => {
      updateItem(item.id, { preview: target });
      reduxDispatch(updateItemPreview(item as Parameters<typeof updateItemPreview>[0]));
    });
    const results = await Promise.allSettled(
      toChange.map((item) =>
        updateResourceProjectPreviewStatus(item.id, item.name || item.title, target),
      ),
    );
    const failed: NormalizedItem[] = [];
    results.forEach((r, idx) => { if (r.status === 'rejected') failed.push(toChange[idx]); });
    // Revert items that failed (updateItemPreview flips the passed item's preview)
    failed.forEach((item) => {
      updateItem(item.id, { preview: !target });
      reduxDispatch(updateItemPreview({ ...item, preview: target } as Parameters<typeof updateItemPreview>[0]));
    });
    setPreviewBusy(false);
    if (failed.length) {
      notify({ type: 'error', message: `Could not update ${failed.length} of ${toChange.length} files` });
    } else {
      notify({ type: 'success', message: target ? 'Files added to preview' : 'Files hidden from preview' });
    }
  };

  if (!permissions.canEdit && !permissions.canDelete) return null;

  return (
    <div className="flex items-center gap-2 border-b border-border px-1 py-2">
      {!hasSelection ? (
        <Button variant="ghost" size="sm" onClick={selectAll}>
          Select all
        </Button>
      ) : (
        <>
          <span className="text-sm text-muted-foreground">{count} selected</span>
          <Button variant="ghost" size="sm" onClick={deselectAll}>
            <X className="h-4 w-4" />
          </Button>
          <div className="h-4 border-l border-border" />

          {permissions.canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openModal('delete', { items: selectedItemsArray })}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
          {allResources && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadItems(selectedItemsArray)}
            >
              <Download className="h-4 w-4" /> Download
            </Button>
          )}
          {permissions.canMove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openModal('move', { items: selectedItemsArray })}
            >
              <Move className="h-4 w-4" /> Move
            </Button>
          )}
          {permissions.canCopy && allResources && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openModal('copy', { items: selectedItemsArray })}
            >
              <Copy className="h-4 w-4" /> Copy
            </Button>
          )}
          {permissions.canShare && allResources && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openModal('shareFile', { items: selectedItemsArray })}
            >
              <Share2 className="h-4 w-4" /> Share
            </Button>
          )}
          {showPreviewActions && (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={previewBusy}
                onClick={() => setPreviewForSelected(true)}
                title="Show selected files in the project's main view"
              >
                <Eye className="h-4 w-4" /> Preview
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={previewBusy}
                onClick={() => setPreviewForSelected(false)}
                title="Hide selected files from the project's main view"
              >
                <EyeOff className="h-4 w-4" /> Unpreview
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}
